import { WithClassName } from 'src/utils/UtilityTypes';

/**
 * Decorative glow / grid backdrop for the error page (ported from Figma).
 * Rendered inline so the mask + blur/noise filter render reliably across
 * browsers (an external SVG used as a CSS background does not).
 */
export default function ErrorPageBackdrop({ className }: WithClassName) {
	return (
		<svg
			aria-hidden
			className={className}
			width="100%"
			height="100%"
			viewBox="0 0 760 336"
			fill="none"
			preserveAspectRatio="xMidYMid meet"
			xmlns="http://www.w3.org/2000/svg"
		>
			<mask
				id="error-backdrop-mask"
				style={{ maskType: 'alpha' }}
				maskUnits="userSpaceOnUse"
				x="0"
				y="7"
				width="760"
				height="322"
			>
				<ellipse
					cx="380"
					cy="168"
					rx="380"
					ry="161"
					fill="url(#error-backdrop-radial-mask)"
				/>
			</mask>
			<g mask="url(#error-backdrop-mask)">
				<g filter="url(#error-backdrop-blur)">
					<ellipse
						cx="380"
						cy="167.5"
						rx="380"
						ry="160.5"
						fill="url(#error-backdrop-radial-glow)"
					/>
				</g>
				<g opacity="0.5">
					<path
						d="M288.918 128.5H694.381"
						stroke="url(#error-backdrop-line-2)"
					/>
					<path d="M140 164L594 164" stroke="url(#error-backdrop-line-3)" />
					<path d="M339 200L538 200" stroke="url(#error-backdrop-line-4)" />
					<path d="M220.361 267H497.526" stroke="url(#error-backdrop-line-5)" />
					<path d="M455.412 241H732.577" stroke="url(#error-backdrop-line-6)" />
					<path d="M142.01 221H493.608" stroke="url(#error-backdrop-line-7)" />
					<path d="M171.392 88H576.856" stroke="url(#error-backdrop-line-8)" />
				</g>
			</g>
			{/* Decorative diamonds (rotated squares). */}
			{[
				[283.657, 83],
				[411.657, 262],
				[161.657, 158],
				[556.657, 123],
				[471.657, 83],
				[510.657, 158],
			].map(([x, y]) => (
				<rect
					key={`${x}-${y}`}
					x={x}
					y={y}
					width="8"
					height="8"
					transform={`rotate(45 ${x} ${y})`}
					style={{ fill: 'var(--button-disabled, #20222d)' }}
				/>
			))}
			<defs>
				<filter
					id="error-backdrop-blur"
					x="-80"
					y="-73"
					width="920"
					height="481"
					filterUnits="userSpaceOnUse"
					colorInterpolationFilters="sRGB"
				>
					<feFlood floodOpacity="0" result="BackgroundImageFix" />
					<feBlend
						mode="normal"
						in="SourceGraphic"
						in2="BackgroundImageFix"
						result="shape"
					/>
					<feGaussianBlur stdDeviation="40" result="effect1_foregroundBlur" />
					<feTurbulence
						type="fractalNoise"
						baseFrequency="2 2"
						stitchTiles="stitch"
						numOctaves="3"
						result="noise"
						seed="1814"
					/>
					<feColorMatrix
						in="noise"
						type="luminanceToAlpha"
						result="alphaNoise"
					/>
					<feComponentTransfer in="alphaNoise" result="coloredNoise1">
						<feFuncA
							type="discrete"
							tableValues="0 0 0 0 0 0 0 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 "
						/>
					</feComponentTransfer>
					<feComposite
						operator="in"
						in2="effect1_foregroundBlur"
						in="coloredNoise1"
						result="noise1Clipped"
					/>
					<feFlood floodColor="rgba(44, 59, 36, 0.5)" result="color1Flood" />
					<feComposite
						operator="in"
						in2="noise1Clipped"
						in="color1Flood"
						result="color1"
					/>
					<feMerge>
						<feMergeNode in="effect1_foregroundBlur" />
						<feMergeNode in="color1" />
					</feMerge>
				</filter>
				<radialGradient
					id="error-backdrop-radial-mask"
					cx="0"
					cy="0"
					r="1"
					gradientUnits="userSpaceOnUse"
					gradientTransform="translate(380 168) rotate(90) scale(161 380)"
				>
					<stop stopColor="white" />
					<stop offset="0.725962" stopColor="white" stopOpacity="0.8" />
					<stop offset="0.961538" stopColor="white" stopOpacity="0" />
				</radialGradient>
				<radialGradient
					id="error-backdrop-radial-glow"
					cx="0"
					cy="0"
					r="1"
					gradientUnits="userSpaceOnUse"
					gradientTransform="translate(380 167.5) rotate(90) scale(160.5 380)"
				>
					<stop style={{ stopColor: 'var(--button-secondary-bg, #272a38)' }} />
					<stop
						offset="0.961538"
						style={{ stopColor: 'var(--main-bg, #05060c)' }}
					/>
				</radialGradient>
				{[
					{
						id: 'error-backdrop-line-2',
						x1: '288.918',
						y1: '129',
						x2: '694.381',
						y2: '129',
					},
					{
						id: 'error-backdrop-line-3',
						x1: '140',
						y1: '164.5',
						x2: '594',
						y2: '164.5',
					},
					{
						id: 'error-backdrop-line-4',
						x1: '339',
						y1: '200.5',
						x2: '538',
						y2: '200.5',
					},
					{
						id: 'error-backdrop-line-5',
						x1: '220.361',
						y1: '267.5',
						x2: '497.526',
						y2: '267.5',
					},
					{
						id: 'error-backdrop-line-6',
						x1: '455.412',
						y1: '241.5',
						x2: '732.577',
						y2: '241.5',
					},
					{
						id: 'error-backdrop-line-7',
						x1: '142.01',
						y1: '221.5',
						x2: '493.608',
						y2: '221.5',
					},
					{
						id: 'error-backdrop-line-8',
						x1: '171.392',
						y1: '88.5',
						x2: '576.856',
						y2: '88.5',
					},
				].map(({ id, x1, y1, x2, y2 }) => (
					<linearGradient
						key={id}
						id={id}
						x1={x1}
						y1={y1}
						x2={x2}
						y2={y2}
						gradientUnits="userSpaceOnUse"
					>
						<stop stopColor="#05060C" stopOpacity="0" />
						<stop offset="0.413462" stopColor="#15BC25" />
						<stop offset="1" stopColor="#05060C" stopOpacity="0" />
					</linearGradient>
				))}
			</defs>
		</svg>
	);
}
